/**
 * Pre-configured demo scenarios for sales presentations.
 * Each scenario walks through: Meet card → pre-filled form → matched banks → lender connect.
 */

export const DANIEL_SCENARIO = {
  id: "daniel",
  meet: {
    name: "Daniel Rolle",
    age: 32,
    photo_initials: "DR",
    role: "Government Employee",
    employer: "Ministry of Education",
    monthly_salary: 5200,
    existing_debts: 680,
    debt_breakdown: [
      { label: "Credit card minimums", amount: 420 },
      { label: "Consumer loan payment", amount: 260 },
    ],
    loan_ask: 50000,
    loan_type_label: "Personal Loan",
    goal: "Consolidate cards + fund home improvements",
    bio:
      "Daniel is a 32-year-old Bahamian public servant who has been with the Ministry of Education for 6 years. He wants to consolidate his credit card debt and take on a home renovation project.",
  },
  form_data: {
    loan_type: "personal",
    employment_type: "government",
    employment_status: "full_time",
    employer_name: "Ministry of Education",
    employment_years: 6,
    employment_months: 0,
    gross_monthly_salary: 5200,
    other_income: 0,
    existing_debts: {
      credit_card_payment: 420,
      personal_loan_payment: 260,
      mortgage_payment: 0,
      other_debts: 0,
    },
    desired_loan_amount: 50000,
    age: 32,
    citizenship_status: "citizen",
    dependents: 1,
    credit_authorization: true,
    full_name: "Daniel Rolle",
    email: "daniel.rolle@moe.gov.bs",
    phone: "2425557823",
  },
  form_steps: [
    {
      title: "Employment Type",
      answer: "Government Employee — Ministry of Education",
      details: [
        { label: "Type", value: "Government" },
        { label: "Employer", value: "Ministry of Education" },
        { label: "Tenure", value: "6 years" },
        { label: "Status", value: "Full-time permanent" },
      ],
    },
    {
      title: "Loan Type",
      answer: "Personal Loan",
      details: [
        { label: "Purpose", value: "Debt consolidation + home improvements" },
      ],
    },
    {
      title: "Employment & Income",
      answer: "$5,200/month gross",
      details: [
        { label: "Gross monthly salary", value: "$5,200" },
        { label: "Other income", value: "$0" },
        { label: "Contract end", value: "N/A (Permanent)" },
      ],
    },
    {
      title: "Personal Info",
      answer: "Daniel Rolle, 32, Bahamian citizen",
      details: [
        { label: "Full name", value: "Daniel Rolle" },
        { label: "Age", value: "32" },
        { label: "Citizenship", value: "Bahamian citizen" },
        { label: "Dependents", value: "1" },
      ],
    },
    {
      title: "Existing Debts",
      answer: "$680/month total",
      details: [
        { label: "Credit cards", value: "$420" },
        { label: "Consumer loan", value: "$260" },
        { label: "Mortgage", value: "$0" },
        { label: "Other", value: "$0" },
      ],
    },
    {
      title: "Loan Amount",
      answer: "$50,000",
      details: [{ label: "Requested amount", value: "$50,000" }],
    },
    {
      title: "Credit Authorization",
      answer: "Signed & authorized",
      details: [
        { label: "TDSR estimate", value: "Under 45%" },
        { label: "Credit pull authorization", value: "Yes" },
        { label: "Signature", value: "On file" },
      ],
    },
  ],
};

export const SARAH_SCENARIO = {
  id: "sarah",
  meet: {
    name: "Sarah Bethel",
    age: 28,
    photo_initials: "SB",
    role: "Marketing Coordinator",
    employer: "Atlantis Paradise Island",
    monthly_salary: 4200,
    existing_debts: 300,
    debt_breakdown: [{ label: "Credit card minimum", amount: 300 }],
    loan_ask: 28000,
    loan_type_label: "Auto Loan",
    goal: "Buy a 2023 Toyota Corolla for the daily commute",
    bio:
      "Sarah is a 28-year-old marketing coordinator at a leading Nassau resort. After 4 years at Atlantis, she's ready to upgrade from public transport to her own reliable car.",
  },
  form_data: {
    loan_type: "car",
    vehicle_type: "gas",
    cash_injection_ready: true,
    employment_type: "private",
    employment_status: "full_time",
    employer_name: "Atlantis Paradise Island",
    employment_years: 4,
    employment_months: 0,
    gross_monthly_salary: 4200,
    other_income: 0,
    existing_debts: {
      credit_card_payment: 300,
      personal_loan_payment: 0,
      mortgage_payment: 0,
      other_debts: 0,
    },
    desired_loan_amount: 28000,
    age: 28,
    citizenship_status: "citizen",
    dependents: 0,
    credit_authorization: true,
    full_name: "Sarah Bethel",
    email: "sarah.bethel@example.com",
    phone: "2425554412",
  },
  form_steps: [
    {
      title: "Employment Type",
      answer: "Private Sector — Atlantis Paradise Island",
      details: [
        { label: "Type", value: "Private" },
        { label: "Employer", value: "Atlantis Paradise Island" },
        { label: "Tenure", value: "4 years" },
        { label: "Status", value: "Full-time permanent" },
      ],
    },
    {
      title: "Loan Type",
      answer: "Auto Loan — Gas vehicle · Cash injection ready",
      details: [
        { label: "Vehicle type", value: "Gas (new)" },
        { label: "Cash injection", value: "Yes — 10% down ready" },
      ],
    },
    {
      title: "Employment & Income",
      answer: "$4,200/month gross",
      details: [
        { label: "Gross monthly salary", value: "$4,200" },
        { label: "Other income", value: "$0" },
      ],
    },
    {
      title: "Personal Info",
      answer: "Sarah Bethel, 28, Bahamian citizen",
      details: [
        { label: "Full name", value: "Sarah Bethel" },
        { label: "Age", value: "28" },
        { label: "Citizenship", value: "Bahamian citizen" },
        { label: "Dependents", value: "0" },
      ],
    },
    {
      title: "Existing Debts",
      answer: "$300/month total",
      details: [
        { label: "Credit cards", value: "$300" },
        { label: "Other", value: "$0" },
      ],
    },
    {
      title: "Loan Amount",
      answer: "$28,000",
      details: [
        { label: "Vehicle purchase price", value: "$31,000" },
        { label: "Requested financing", value: "$28,000 (90% LTV)" },
      ],
    },
    {
      title: "Credit Authorization",
      answer: "Signed & authorized",
      details: [
        { label: "TDSR estimate", value: "Under 40%" },
        { label: "Credit pull authorization", value: "Yes" },
        { label: "Signature", value: "On file" },
      ],
    },
  ],
};

export const MARCUS_SCENARIO = {
  id: "marcus",
  meet: {
    name: "Marcus Ferguson",
    age: 34,
    photo_initials: "MF",
    role: "Government Employee",
    employer: "Bahamas Power & Light",
    monthly_salary: 6800,
    existing_debts: 425,
    debt_breakdown: [{ label: "Credit card minimum", amount: 425 }],
    loan_ask: 285000,
    loan_type_label: "First-Time Home Mortgage",
    goal: "Buy a starter home in Nassau East for his growing family",
    bio:
      "Marcus is a 34-year-old engineer at BPL with 9 years of tenure. He and his wife are expecting their first child and want to purchase their first home — a 3-bed townhouse listed at $300K.",
  },
  form_data: {
    loan_type: "home",
    property_type: "single_family",
    has_property_in_mind: true,
    property_sale_price: 300000,
    down_payment_ready: true,
    is_first_time_buyer: true,
    has_life_insurance: false,
    has_co_applicant: false,
    wants_closing_cost_estimate: true,
    employment_type: "government",
    employment_status: "full_time",
    employer_name: "Bahamas Power & Light",
    employment_years: 9,
    employment_months: 0,
    gross_monthly_salary: 6800,
    other_income: 0,
    existing_debts: {
      credit_card_payment: 425,
      personal_loan_payment: 0,
      mortgage_payment: 0,
      other_debts: 0,
    },
    desired_loan_amount: 285000,
    age: 34,
    citizenship_status: "citizen",
    dependents: 1,
    credit_authorization: true,
    full_name: "Marcus Ferguson",
    email: "marcus.ferguson@example.com",
    phone: "2425558091",
  },
  form_steps: [
    {
      title: "Employment Type",
      answer: "Government Employee — Bahamas Power & Light",
      details: [
        { label: "Type", value: "Government" },
        { label: "Employer", value: "Bahamas Power & Light" },
        { label: "Tenure", value: "9 years" },
        { label: "Status", value: "Full-time permanent" },
      ],
    },
    {
      title: "Loan Type",
      answer: "Home Mortgage — First-time buyer",
      details: [
        { label: "Property type", value: "Residential (townhouse)" },
        { label: "Has property in mind", value: "Yes — Nassau East" },
        { label: "Sale price", value: "$300,000" },
        { label: "Down payment ready", value: "Yes (5% = $15,000)" },
        { label: "First-time buyer", value: "Yes" },
        { label: "Life insurance", value: "No — will secure at closing" },
        { label: "Co-applicant", value: "None" },
      ],
    },
    {
      title: "Employment & Income",
      answer: "$6,800/month gross",
      details: [
        { label: "Gross monthly salary", value: "$6,800" },
        { label: "Other income", value: "$0" },
      ],
    },
    {
      title: "Personal Info",
      answer: "Marcus Ferguson, 34, Bahamian citizen",
      details: [
        { label: "Full name", value: "Marcus Ferguson" },
        { label: "Age", value: "34" },
        { label: "Citizenship", value: "Bahamian citizen" },
        { label: "Dependents", value: "1 (expected)" },
      ],
    },
    {
      title: "Existing Debts",
      answer: "$425/month total",
      details: [
        { label: "Credit cards", value: "$425" },
        { label: "Other", value: "$0" },
      ],
    },
    {
      title: "Loan Amount",
      answer: "$285,000 mortgage ($300K price − $15K down)",
      details: [
        { label: "Property price", value: "$300,000" },
        { label: "Down payment (5%)", value: "$15,000" },
        { label: "Requested mortgage", value: "$285,000" },
      ],
    },
    {
      title: "Credit Authorization",
      answer: "Signed & authorized",
      details: [
        { label: "TDSR estimate", value: "Under 45%" },
        { label: "Credit pull authorization", value: "Yes" },
        { label: "Signature", value: "On file" },
      ],
    },
  ],
};
